// Online Multiplayer Service using WebSockets
// Note: This requires a WebSocket server backend. For development, you can use a service like Socket.io

export class OnlineMultiplayerService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.playerId = null;
    this.gameRoom = null;
    this.listeners = {};
    
    // For development/demo, we'll use a mock mode
    this.mockMode = true; // Set to false when you have a real server
  }

  // Connect to server
  async connect(serverUrl = 'ws://localhost:3001') {
    if (this.mockMode) {
      console.log('[Mock] Simulating connection to server');
      this.connected = true;
      this.playerId = `player-${Date.now()}`;
      return { success: true, playerId: this.playerId };
    }

    return new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(serverUrl);

        this.socket.onopen = () => {
          console.log('Connected to multiplayer server');
          this.connected = true;
          resolve({ success: true });
        };

        this.socket.onmessage = (event) => {
          this.handleMessage(JSON.parse(event.data));
        };

        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };

        this.socket.onclose = () => {
          console.log('Disconnected from server');
          this.connected = false;
          this.emit('disconnected');
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  // Disconnect from server
  disconnect() {
    if (this.socket) {
      this.socket.close();
    }
    this.connected = false;
    this.gameRoom = null;
  }

  // Send message to server
  send(type, data) {
    if (this.mockMode) {
      console.log('[Mock] Sending message:', type, data);
      return;
    }

    if (!this.socket || !this.connected) {
      console.error('Not connected to server');
      return;
    }

    this.socket.send(JSON.stringify({ type, data, playerId: this.playerId }));
  }

  // Handle incoming messages
  handleMessage(message) {
    const { type, data } = message;
    
    console.log('Received message:', type, data);
    
    // Emit event to listeners
    this.emit(type, data);
  }

  // Event listener system
  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }

  emit(event, data) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach(callback => callback(data));
  }

  // Create a game room
  async createRoom(playerName) {
    if (this.mockMode) {
      const roomId = `room-${Date.now()}`;
      this.gameRoom = roomId;
      console.log('[Mock] Created room:', roomId);
      return { success: true, roomId, playerId: this.playerId };
    }

    this.send('create_room', { playerName });
    
    return new Promise((resolve) => {
      this.once('room_created', (data) => {
        this.gameRoom = data.roomId;
        resolve(data);
      });
    });
  }

  // Join a game room
  async joinRoom(roomId, playerName) {
    if (this.mockMode) {
      this.gameRoom = roomId;
      console.log('[Mock] Joined room:', roomId);
      return { success: true, roomId, playerId: this.playerId };
    }

    this.send('join_room', { roomId, playerName });
    
    return new Promise((resolve) => {
      this.once('room_joined', resolve);
    });
  }

  // Leave current room
  leaveRoom() {
    if (this.mockMode) {
      console.log('[Mock] Left room');
      this.gameRoom = null;
      return;
    }

    this.send('leave_room', { roomId: this.gameRoom });
    this.gameRoom = null;
  }

  // Mark player as ready
  setReady(ready = true) {
    this.send('player_ready', { ready, roomId: this.gameRoom });
  }

  // Send game action
  sendAction(action) {
    this.send('game_action', { 
      action, 
      roomId: this.gameRoom,
      playerId: this.playerId 
    });
  }

  // Send updated game state (for host)
  sendGameState(gameState) {
    this.send('game_state_update', { 
      gameState: gameState.serialize(),
      roomId: this.gameRoom 
    });
  }

  // One-time event listener
  once(event, callback) {
    const wrappedCallback = (data) => {
      callback(data);
      this.off(event, wrappedCallback);
    };
    this.on(event, wrappedCallback);
  }

  // Get list of available rooms
  async getRooms() {
    if (this.mockMode) {
      console.log('[Mock] Getting rooms list');
      return { rooms: [] };
    }

    this.send('get_rooms', {});
    
    return new Promise((resolve) => {
      this.once('rooms_list', resolve);
    });
  }
}

// Singleton instance
let serviceInstance = null;

export const getMultiplayerService = () => {
  if (!serviceInstance) {
    serviceInstance = new OnlineMultiplayerService();
  }
  return serviceInstance;
};

export default OnlineMultiplayerService;

